import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import AuthHero from "../../components/auth/AuthHero";
import { mandateService } from "../../services/api";
import { tp_logo, logo } from "../../constants/icons";

const PlayerMandateResponse = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token: paramToken } = useParams();

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const queryToken = searchParams.get("invite_token");

  const {
    token: stateToken,
    playerId: statePlayerId,
    inviteParams,
    rawQuery,
    email,
    inviteSentBy,
    senderType,
    agencyId,
    type,
    verification,
  } = location.state || {};

  const mandateToken = paramToken || stateToken || queryToken;

  const [mandateData, setMandateData] = useState(null);
  const [playerId, setPlayerId] = useState(statePlayerId || null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [birthDateRequired, setBirthDateRequired] = useState(false);
  const [birthDate, setBirthDate] = useState("");

  // Accordion states
  const [isContractOpen, setIsContractOpen] = useState(true);
  const [isAgencyOpen, setIsAgencyOpen] = useState(false);
  const [isPlayerOpen, setIsPlayerOpen] = useState(false);

  const sharedState = useMemo(
    () => ({
      token: mandateToken,
      playerId,
      inviteParams,
      rawQuery,
      email,
      inviteSentBy,
      senderType,
      agencyId,
      type,
      verification,
    }),
    [
      mandateToken,
      playerId,
      inviteParams,
      rawQuery,
      email,
      inviteSentBy,
      senderType,
      agencyId,
      type,
      verification,
    ]
  );

  const basePath = useMemo(
    () =>
      mandateToken
        ? `/player-register-mandate/${mandateToken}`
        : "/player-register-mandate",
    [mandateToken]
  );

  useEffect(() => {
    if (!mandateToken) {
      setLoading(false);
      toast.error("Invalid representation invitation");
      navigate("/player-register-mandate");
      return;
    }

    let isMounted = true;

    const fetchMandateDetails = async () => {
      try {
        const response = await mandateService.getMandateDetailsByToken(
          mandateToken
        );

        if (!isMounted) return;

        if (response.success) {
          setMandateData(response.data);
          if (response.data?.mandate?.player_id) {
            setPlayerId(Number(response.data.mandate.player_id));
          }
        } else {
          toast.error(response.error || "Failed to load representation details");
          navigate(basePath, { replace: true, state: sharedState });
        }
      } catch (error) {
        if (!isMounted) return;
        console.error("Fetch mandate error:", error);
        toast.error("Failed to load representation details");
        navigate(basePath, { replace: true, state: sharedState });
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchMandateDetails();

    return () => {
      isMounted = false;
    };
  }, [basePath, mandateToken, navigate, sharedState]);

  const handleAcceptSuccess = (responseData) => {
    const nextMandateData = responseData || mandateData;
    const successState = {
      mandateData: nextMandateData,
      agentName:
        nextMandateData?.mandate?.agent_name ||
        mandateData?.mandate?.agent_name ||
        "your agent",
      playerId:
        nextMandateData?.player_id ||
        nextMandateData?.mandate?.player_id ||
        playerId,
      inviteParams,
      rawQuery,
    };

    navigate(`${basePath}/success`, { state: successState });
  };

  const handleAccept = async () => {
    setSubmitting(true);
    try {
      const response = await mandateService.respondToMandate(
        mandateToken,
        "accept"
      );

      if (response.success) {
        handleAcceptSuccess(response.data);
      } else {
        if (
          response.error &&
          response.error.toLowerCase().includes("birthdate is required")
        ) {
          setBirthDateRequired(true);
          toast.error("Please enter your birth date to continue.");
        } else if (response.code === "ID_REQUIRED") {
          toast.error(
            "Identity verification required. Please complete verification."
          );
          navigate(`${basePath}/verify`, { state: sharedState });
        } else {
          toast.error(response.error || "Failed to accept representation");
        }
      }
    } catch (error) {
      console.error("Accept mandate error:", error);
      toast.error("Failed to accept representation. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleBirthDateSubmit = async () => {
    if (!birthDate) {
      toast.error("Please select your birth date.");
      return;
    }

    setSubmitting(true);
    try {
      const response = await mandateService.respondToMandate(
        mandateToken,
        "accept",
        null,
        null,
        birthDate
      );

      if (response.success) {
        handleAcceptSuccess(response.data);
      } else if (response.code === "ID_REQUIRED") {
        toast.error(
          "Identity verification required. Please complete verification."
        );
        navigate(`${basePath}/verify`, { state: sharedState });
      } else {
        toast.error(response.error || "Failed to accept representation");
      }
    } catch (error) {
      console.error("Accept mandate with birth date error:", error);
      toast.error("Failed to accept representation. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = async () => {
    setSubmitting(true);
    try {
      const response = await mandateService.respondToMandate(
        mandateToken,
        "decline"
      );

      if (response.success) {
        navigate(`${basePath}/declined`, {
          state: {
            inviteParams,
            rawQuery,
          },
        });
      } else {
        toast.error(response.error || "Failed to decline representation");
      }
    } catch (error) {
      console.error("Decline mandate error:", error);
      toast.error("Failed to decline representation. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate(`${basePath}/verify`, { state: sharedState });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="flex min-h-screen">
        <div className="flex-1 bg-white flex items-center justify-center max-w-2xl mx-auto p-4">
          <div className="text-center">
            <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-gray-500">Loading representation details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!mandateData) {
    return (
      <div className="flex min-h-screen">
        {/* <AuthHero /> */}
        <div className="flex-1 bg-white flex items-center justify-center max-w-2xl mx-auto p-4">
          <div className="text-center">
            <p className="text-red-500">Failed to load representation details</p>
            <button
              onClick={() => navigate("/player-register-mandate")}
              className="mt-4 px-4 py-2 bg-[#2283FF] text-white rounded-lg"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  const mandate = mandateData?.mandate || {};
  const agent = mandateData?.agent || {};
  const agency = mandateData?.agency || {};
  const player = mandateData?.player || {};

  return (
    <div className="flex min-h-screen">
      {/* <AuthHero /> */}
      {/* min-w-0: as a flex item this column keeps min-width:auto, so without
          it the card's min-content (436px with the accordions open) pushes the
          whole page sideways on a phone. */}
      <div className="flex-1 min-w-0 bg-[#F8FAFC] flex flex-col justify-center py-12">
        <div className="w-full max-w-2xl px-6 mx-auto">
          {/* Logo at top center */}

          <div className="bg-white rounded-xl border border-gray-300 p-6 shadow-sm">
            <div className="flex items-center mb-2">
              <button
                onClick={handleBack}
                className="flex items-center gap-2 text-[#2283FF] hover:text-[#1b6dd2] transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="w-5 h-5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15.75 19.5L8.25 12l7.5-7.5"
                  />
                </svg>
                Back
              </button>
            </div>

            <div className="w-full mb-2">
              <div className="flex justify-center">
                <img src={logo} alt="Transfer Pitch" className="h-12" />
              </div>
            </div>

            <div className="text-center mb-8">
              <h2 className="text-[24px] leading-[32px] font-semibold text-gray-900 mb-3">
                Representation Agreement Details
              </h2>
              <p className="text-[16px] leading-[24px] text-gray-600">
                Please review the representation details and choose your response
              </p>
            </div>

            {/* Contract Details Accordion */}
            <div className="mb-4">
              <button
                onClick={() => setIsContractOpen(!isContractOpen)}
                className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#2283FF] rounded-lg flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  </div>
                  <span className="font-semibold text-gray-900">
                    Contract Details
                  </span>
                </div>
                <svg
                  className={`w-5 h-5 text-gray-500 transition-transform ${
                    isContractOpen ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {isContractOpen && (
                <div className="mt-2 p-4 border border-gray-200 rounded-lg bg-white space-y-4">
                  <div className="flex justify-between items-center ">
                    <div className="flex flex-col gap-1 w-[50%]">
                      <span className="text-sm text-gray-500">
                        Contract Type
                      </span>
                      <span className="font-medium text-gray-900 capitalize">
                        {mandate.contract_type?.replace(/_/g, " ") || "—"}
                      </span>
                    </div>

                    {mandate.start_date && (
                      <div className="flex flex-col gap-1 w-[50%]">
                        <span className="text-sm text-gray-500">
                          Start Date
                        </span>
                        <span className="font-medium text-gray-900">
                          {formatDate(mandate.start_date)}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-between items-center ">
                    {mandate.end_date && (
                      <div className="flex flex-col gap-1 w-[50%]">
                        <span className="text-sm text-gray-500">End Date</span>
                        <span className="font-medium text-gray-900">
                          {formatDate(mandate.end_date)}
                        </span>
                      </div>
                    )}

                    <div className="flex flex-col gap-1  w-[50%]">
                      <span className="text-sm text-gray-500">Exclusivity</span>
                      <span className="font-medium text-gray-900">
                        {mandate.is_exclusive ? "Exclusive" : "Non-Exclusive"}
                      </span>
                    </div>
                  </div>

                  {Array.isArray(mandate.countries) &&
                    mandate.countries.length > 0 && (
                      <div className="flex flex-col gap-2">
                        <span className="text-sm text-gray-500">Countries</span>
                        <div className="flex flex-wrap gap-2">
                          {mandate.countries.map((country) => (
                            <span
                              key={country.id || country.code || country.name}
                              className="inline-flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full text-sm text-gray-700"
                            >
                              {country.image_path && (
                                <img
                                  src={country.image_path}
                                  alt={country.name}
                                  className="w-4 h-4 rounded-full object-cover"
                                />
                              )}
                              {country.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                  {mandate.notes && (
                    <div className="flex flex-col gap-2">
                      <span className="text-sm text-gray-500">Notes</span>
                      <p className="text-gray-700 whitespace-pre-line bg-gray-50 rounded-md p-3 text-sm">
                        {mandate.notes}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Agency & Agent Details Accordion */}
            <div className="mb-4">
              <button
                onClick={() => setIsAgencyOpen(!isAgencyOpen)}
                className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#2283FF] rounded-lg flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                      />
                    </svg>
                  </div>
                  <span className="font-semibold text-gray-900">
                    Agency & Agent Details
                  </span>
                </div>
                <svg
                  className={`w-5 h-5 text-gray-500 transition-transform ${
                    isAgencyOpen ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {isAgencyOpen && (
                <div className="mt-2 p-4 border border-gray-200 rounded-lg bg-white space-y-4">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 sm:gap-0">
                    {agent.name && (
                      <div className="flex flex-col gap-2 w-full sm:w-[50%]">
                        <span className="text-sm text-gray-500">Agent</span>
                        <div className="flex items-center gap-3">
                          {agent.profile_picture_url ? (
                            <img
                              src={agent.profile_picture_url}
                              alt={agent.name}
                              className="w-12 h-12 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                              <svg
                                className="w-6 h-6 text-gray-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                />
                              </svg>
                            </div>
                          )}
                          <span
                            className="font-medium text-gray-900 text-nowrap whitespace-nowrap truncate"
                            title={agent.name}
                          >
                            {agent.name}
                          </span>
                        </div>
                      </div>
                    )}

                    {agency.name && (
                      <div className="flex flex-col gap-2 w-full sm:w-[50%]">
                        <span className="text-sm text-gray-500">Agency</span>
                        <div className="flex items-center gap-3">
                          {agency.profile_picture_url ? (
                            <img
                              src={agency.profile_picture_url}
                              alt={agency.name}
                              className="w-12 h-12 rounded-lg object-cover"
                            />
                          ) : (
                            <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                              <svg
                                className="w-6 h-6 text-gray-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                                />
                              </svg>
                            </div>
                          )}
                          <span
                            className="font-medium text-gray-900 text-nowrap whitespace-nowrap truncate"
                            title={agency.name}
                          >
                            {agency.name}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Player Details Accordion */}
            <div className="mb-6">
              <button
                onClick={() => setIsPlayerOpen(!isPlayerOpen)}
                className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#2283FF] rounded-lg flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  </div>
                  <span className="font-semibold text-gray-900">
                    Player Details
                  </span>
                </div>
                <svg
                  className={`w-5 h-5 text-gray-500 transition-transform ${
                    isPlayerOpen ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {isPlayerOpen && (
                <div className="mt-2 p-4 border border-gray-200 rounded-lg bg-white space-y-4">
                  {player.name && (
                    <div className="flex flex-col gap-2">
                      <span className="text-sm text-gray-500">Player Name</span>
                      <div className="flex items-center gap-3">
                        {player.image_url ? (
                          <img
                            src={player.image_url}
                            alt={player.name}
                            className="w-12 h-12 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                            <svg
                              className="w-6 h-6 text-gray-400"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                              />
                            </svg>
                          </div>
                        )}
                        <span className="font-medium text-gray-900">
                          {player.name}
                        </span>
                      </div>
                    </div>
                  )}

                  {player.club_name && (
                    <div className="flex flex-col gap-2">
                      <span className="text-sm text-gray-500">
                        Current Club
                      </span>
                      <div className="flex items-center gap-3">
                        {player.club_logo ? (
                          <img
                            src={player.club_logo}
                            alt={player.club_name}
                            className="w-10 h-10 rounded-lg object-contain"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                            <svg
                              className="w-5 h-5 text-gray-400"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                              />
                            </svg>
                          </div>
                        )}
                        <span className="font-medium text-gray-900">
                          {player.club_name}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {birthDateRequired && (
              <div className="mb-6 p-4 border border-yellow-200 bg-yellow-50 rounded-lg">
                <h4 className="text-sm font-semibold text-yellow-900 mb-2">
                  Birth date required
                </h4>
                <p className="text-sm text-yellow-800 mb-3">
                  We need your birth date to create your player profile.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
                  <div className="w-full sm:w-auto">
                    <label className="text-xs text-gray-600 mb-1 block">
                      Birth date
                    </label>
                    <input
                      type="date"
                      value={birthDate}
                      onChange={(e) => setBirthDate(e.target.value)}
                      className="w-full sm:w-60 border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  <button
                    onClick={handleBirthDateSubmit}
                    disabled={submitting}
                    className="px-4 py-2 bg-[#2283FF] text-white rounded-md text-sm disabled:opacity-50"
                  >
                    {submitting ? "Submitting..." : "Continue"}
                  </button>
                </div>
              </div>
            )}

            {/* Stacked below sm: side by side the row needs 292px min-content
                ("Representation" cannot break) but a 375px phone offers 279. */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={handleDecline}
                disabled={submitting}
                className="flex-1 py-4 px-6 border-2 border-gray-300 hover:border-red-400 text-gray-700 hover:text-red-600 rounded-lg transition-colors font-medium text-[16px] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? "Processing..." : "Decline"}
              </button>

              <button
                onClick={handleAccept}
                disabled={submitting}
                className={`flex-1 py-4 px-6 rounded-lg transition-colors font-medium text-[16px] flex items-center justify-center gap-2 ${
                  submitting
                    ? "bg-[#2283FF50] text-white cursor-not-allowed"
                    : "bg-[#2283FF] text-white"
                }`}
              >
                {submitting && (
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                )}
                {submitting ? "Processing..." : "Accept Representation"}
              </button>
            </div>

            <p className="text-center text-sm text-gray-500 mt-6">
              By accepting, you agree to be represented by the agent under the
              terms specified above
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlayerMandateResponse;
